// services/battleWebSocketManager.js (COMPLETE - unchanged from original)
import echo from "../echo";
import { store } from "../redux/store";
import {
  addEvent,
  setConnected,
  addChannel,
  removeChannel,
  incrementReconnectAttempts,
  resetReconnectAttempts,
} from "../redux/slices/battleSlice";

class BattleWebSocketManager {
  constructor() {
    this.channels = new Map();
    this.unsubscribeTimers = new Map();
    this.initialized = false;
    this.reconnectTimer = null;
    this.maxReconnectAttempts = 5;
    this.keepAliveMinutes = 10;
  }

  initialize() {
    if (this.initialized) return;

    if (echo.connector?.pusher) {
      echo.connector.pusher.connection.bind("connected", () => {
        store.dispatch(setConnected(true));
        store.dispatch(resetReconnectAttempts());
        this.reconnectAllChannels();
      });

      echo.connector.pusher.connection.bind("disconnected", () => {
        store.dispatch(setConnected(false));
        this.handleDisconnection();
      });

      echo.connector.pusher.connection.bind("error", (err) => {
        console.error("WebSocket Error:", err);
      });
    }

    this.initialized = true;
  }

  subscribeToGeneralChannel() {
    const channelName = "battle";

    if (this.channels.has(channelName)) {
      return;
    }

    if (!echo?.connector?.pusher) {
      console.error("Pusher connector not available");
      return;
    }

    try {
      const channel = echo.channel(channelName);

      channel.listen(".pusher:subscription_succeeded", () => {});

      channel.listen(".pusher:subscription_error", (err) => {
        console.error(
          `Subscription error on general battle channel`,
          err
        );
      });

      const globalHandler = (eventName, data) => {
        if (data?.type) {
          store.dispatch(
            addEvent({
              ...data,
              _receivedAt: Date.now(),
              _channel: channelName,
              _eventName: eventName,
              _isGeneralChannel: true,
            })
          );
        }
      };

      channel.subscription.bind_global(globalHandler);

      this.channels.set(channelName, {
        pusherChannel: channel,
        globalHandler,
        refCount: 1,
        subscribedAt: Date.now(),
        isGeneral: true,
      });

      store.dispatch(addChannel(channelName));
    } catch (error) {
      console.error(
        `Error subscribing to general battle channel`,
        error
      );
    }
  }

  unsubscribeFromGeneralChannel() {
    const channelName = "battle";
    this._forceUnsubscribe(channelName);
  }

  listenToGeneralEvent(eventName, callback) {
    const channelName = "battle";

    if (!this.channels.has(channelName)) {
      this.subscribeToGeneralChannel();
    }

    const channel = this.channels.get(channelName);
    if (channel?.pusherChannel) {
      channel.pusherChannel.listen(eventName, (data) => {
        if (callback) callback(data);
      });
    }
  }

  listenToBattleCreated(callback) {
    this.listenToGeneralEvent("BattleCreated", callback);
  }

  listenToBattleUpdated(callback) {
    this.listenToGeneralEvent("BattleUpdated", callback);
  }

  listenToBattleDeleted(callback) {
    this.listenToGeneralEvent("BattleDeleted", callback);
  }

  subscribe(battleUuid) {
    if (!battleUuid) return;

    const channelName = `battle.${battleUuid}`;

    if (this.unsubscribeTimers.has(channelName)) {
      clearTimeout(this.unsubscribeTimers.get(channelName));
      this.unsubscribeTimers.delete(channelName);
    }

    if (this.channels.has(channelName)) {
      this.channels.get(channelName).refCount++;
      return;
    }

    if (!echo?.connector?.pusher) {
      console.error("Pusher connector not available");
      return;
    }

    try {
      const channel = echo.channel(channelName);

      channel.listen(".pusher:subscription_succeeded", () => {});

      channel.listen(".pusher:subscription_error", (err) => {
        console.error(`Subscription error on ${channelName}`, err);
      });

      const globalHandler = (eventName, data) => {
        if (data?.type) {
          store.dispatch(
            addEvent({
              ...data,
              _receivedAt: Date.now(),
              _channel: channelName,
              _eventName: eventName,
            })
          );
        }
      };

      channel.subscription.bind_global(globalHandler);

      this.channels.set(channelName, {
        pusherChannel: channel,
        globalHandler,
        refCount: 1,
        subscribedAt: Date.now(),
        battleUuid,
      });

      store.dispatch(addChannel(channelName));
    } catch (error) {
      console.error(`Error subscribing to ${channelName}`, error);
    }
  }

  unsubscribe(battleUuid) {
    if (!battleUuid) return;

    const channelName = `battle.${battleUuid}`;
    if (!this.channels.has(channelName)) return;

    const channel = this.channels.get(channelName);
    channel.refCount--;

    if (channel.refCount <= 0) {
      const timerId = setTimeout(() => {
        this._forceUnsubscribe(channelName);
      }, this.keepAliveMinutes * 60 * 1000);

      this.unsubscribeTimers.set(channelName, timerId);
    }
  }

  _forceUnsubscribe(channelName) {
    if (!this.channels.has(channelName)) return;

    const channel = this.channels.get(channelName);

    if (channel.pusherChannel?.subscription && channel.globalHandler) {
      channel.pusherChannel.subscription.unbind_global(
        channel.globalHandler
      );
    }

    echo.leave(channelName);
    this.channels.delete(channelName);
    this.unsubscribeTimers.delete(channelName);
    store.dispatch(removeChannel(channelName));
  }

  forceUnsubscribe(battleUuid) {
    if (!battleUuid) return;

    const channelName = `battle.${battleUuid}`;

    if (this.unsubscribeTimers.has(channelName)) {
      clearTimeout(this.unsubscribeTimers.get(channelName));
      this.unsubscribeTimers.delete(channelName);
    }

    this._forceUnsubscribe(channelName);
  }

  handleDisconnection() {
    const state = store.getState();
    const attempts = state.battle.websocket.reconnectAttempts;

    if (attempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      return;
    }

    store.dispatch(incrementReconnectAttempts());

    const delay = Math.min(2000 * Math.pow(2, attempts), 32000);

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      if (echo.connector?.pusher) {
        echo.connector.pusher.connect();
      }
    }, delay);
  }

  reconnectAllChannels() {
    if (this.channels.size === 0) return;

    const channelsToReconnect = Array.from(this.channels.entries());
    this.channels.clear();

    channelsToReconnect.forEach(([channelName, data]) => {
      if (data.isGeneral) {
        this.subscribeToGeneralChannel();
        return;
      }

      if (data.battleUuid) {
        this.subscribe(data.battleUuid);

        const restored = this.channels.get(
          `battle.${data.battleUuid}`
        );
        if (restored) {
          restored.refCount = data.refCount;
        }
      }
    });
  }

  isConnected() {
    return (
      echo.connector?.pusher?.connection?.state === "connected"
    );
  }

  getActiveChannels() {
    return Array.from(this.channels.keys());
  }

  getChannelInfo(battleUuid) {
    const channelName = battleUuid
      ? `battle.${battleUuid}`
      : "battle";
    return this.channels.get(channelName);
  }

  isSubscribedToGeneral() {
    return this.channels.has("battle");
  }

  cleanup() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.unsubscribeTimers.forEach(clearTimeout);
    this.unsubscribeTimers.clear();

    this.channels.forEach((channel, channelName) => {
      if (
        channel.pusherChannel?.subscription &&
        channel.globalHandler
      ) {
        channel.pusherChannel.subscription.unbind_global(
          channel.globalHandler
        );
      }
      echo.leave(channelName);
    });

    this.channels.clear();
    store.dispatch(setConnected(false));
  }
}

export const battleWSManager = new BattleWebSocketManager();